import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/shared/hooks/use-toast';
import { useTrainingPartnersFromData } from '@/features/calendar/hooks/useTrainingPartners';
import { usePartnerFeedback } from '@/features/calendar/hooks/usePartnerFeedback';
import { usePartnerNotes } from '@/features/calendar/hooks/usePartnerNotes';
import { useTechniques } from '@/shared/hooks/useTechniques';
import { Training, TrainingPartner } from '@/types/trainings';

export interface IUseTrainingNotesModalParams {
  isOpen: boolean;
  training: Training | null;
  notesData?: any;
  onDataSaved?: () => void;
}

export interface IUseTrainingNotesModalReturn {
  // Form state
  fullNotes: string;
  setFullNotes: (notes: string) => void;
  shareWithCommunity: boolean;
  setShareWithCommunity: (share: boolean) => void;
  privacyLevel: 'public' | 'connections' | 'gym';
  setPrivacyLevel: (level: 'public' | 'connections' | 'gym') => void;
  overallRating: number | null;
  setOverallRating: (rating: number | null) => void;
  selectedTechniques: string[];
  setSelectedTechniques: (techniques: string[]) => void;
  showTechniques: boolean;
  setShowTechniques: (show: boolean) => void;
  openCategories: string[];
  setOpenCategories: (categories: string[]) => void;
  
  // Partners state
  localPartners: TrainingPartner[];
  partnersLoading: boolean;
  partnersSaving: boolean;
  partnerSearchValue: any;
  setPartnerSearchValue: (value: any) => void;
  showAllPartners: boolean;
  setShowAllPartners: (show: boolean) => void;
  partnerNotes: Map<string, any>;
  reloadingPartnerNotes: boolean;
  addPartner: (partner: TrainingPartner) => void;
  removePartner: (partnerId: string) => void;
  savePartners: (partners: TrainingPartner[]) => Promise<TrainingPartner[]>;
  reloadPartnerNotes: (partnerKey: string) => Promise<void>;
  updatePartnerNotes: (partnerKey: string, notes: any) => void;
  hasPartnerNotes: (partner: TrainingPartner) => boolean;
  getPartnerNotes: (partner: TrainingPartner) => any;
  
  // Roll categories state
  rollCategories: Map<string, import('@/features/calendar/components/RollCategoriesSection').IRollCategories>;
  rollCategoriesOpenCategories: Map<string, string[]>;
  setRollCategoriesOpenCategoriesForPartner: (partnerKey: string, categories: string[] | ((prev: string[]) => string[])) => void;
  handleRollCategoryChange: (partnerKey: string, category: string, value: string) => void;
  getRollCategories: (partner: TrainingPartner) => import('@/features/calendar/components/RollCategoriesSection').IRollCategories;
  showRollCategories: Map<string, boolean>;
  setShowRollCategoriesForPartner: (partnerKey: string, show: boolean | ((prev: boolean) => boolean)) => void;
  getShowRollCategories: (partner: TrainingPartner) => boolean;
  
  // Feedback modal state
  currentFeedbackPartner: TrainingPartner | null;
  setCurrentFeedbackPartner: (partner: TrainingPartner | null) => void;
  existingFeedback: any;
  setExistingFeedback: (feedback: any) => void;
  showFeedbackModal: boolean;
  setShowFeedbackModal: (show: boolean) => void;
  handleFeedbackModalClose: (wasSaved?: boolean) => Promise<void>;
  
  // Instructor state
  instructor: any;
  setInstructor: (instructor: any) => void;
  instructorLoading: boolean;
  setInstructorLoading: (loading: boolean) => void;
  inputValue: string;
  setInputValue: (value: string) => void;
  filteredInstructors: any[];
  setFilteredInstructors: (instructors: any[]) => void;
  showManualOption: boolean;
  setShowManualOption: (show: boolean) => void;
  
  // Other state
  isUpdatingStatus: boolean;
  setIsUpdatingStatus: (updating: boolean) => void;
  isDeleteModalOpen: boolean;
  setIsDeleteModalOpen: (open: boolean) => void;
  saving: boolean;
  setSaving: (saving: boolean) => void;
  
  // Data
  data: any;
  techniqueCategories: any;
  techniquesLoading: boolean;
  
  // Actions
  handleSave: () => Promise<void>;
  handlePartnerSelect: (partner: TrainingPartner | string | null) => void;
}

export const useTrainingNotesModal = ({
  isOpen,
  training,
  notesData: propNotesData,
  onDataSaved,
}: IUseTrainingNotesModalParams): IUseTrainingNotesModalReturn => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Form state
  const [fullNotes, setFullNotes] = useState<string>('');
  const [shareWithCommunity, setShareWithCommunity] = useState<boolean>(false);
  const [privacyLevel, setPrivacyLevel] = useState<'public' | 'connections' | 'gym'>('public');
  const [overallRating, setOverallRating] = useState<number | null>(null);
  const [selectedTechniques, setSelectedTechniques] = useState<string[]>([]);
  const [showTechniques, setShowTechniques] = useState<boolean>(false);
  const [openCategories, setOpenCategories] = useState<string[]>([]);
  
  // Partners state
  const [partnerSearchValue, setPartnerSearchValue] = useState<any>(null);
  const [showAllPartners, setShowAllPartners] = useState<boolean>(false);
  const [partnerNotes, setPartnerNotes] = useState<Map<string, any>>(new Map());
  const [reloadingPartnerNotes, setReloadingPartnerNotes] = useState<boolean>(false);
  // Roll categories state - Map of partner key to roll categories
  const [rollCategories, setRollCategories] = useState<Map<string, import('@/features/calendar/components/RollCategoriesSection').IRollCategories>>(new Map());
  const [rollCategoriesOpenCategoriesState, setRollCategoriesOpenCategories] = useState<Map<string, string[]>>(new Map());
  const [showRollCategories, setShowRollCategories] = useState<Map<string, boolean>>(new Map());
  const [localPartners, setLocalPartners] = useState<TrainingPartner[]>([]);
  const [partnersRefreshKey, setPartnersRefreshKey] = useState<number>(0);
  
  // Feedback modal state
  const [currentFeedbackPartner, setCurrentFeedbackPartner] = useState<TrainingPartner | null>(null);
  const [existingFeedback, setExistingFeedback] = useState<any>(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState<boolean>(false);
  const [feedbackReloadKey, setFeedbackReloadKey] = useState<number>(0);
  
  // Use ref to track if feedback modal is open (to prevent useEffect from resetting state)
  const feedbackModalOpenRef = useRef<boolean>(false);
  
  // Track if we've already loaded initial data for this training
  const hasLoadedInitialDataRef = useRef<string | null>(null);
  
  // Update ref when showFeedbackModal changes
  useEffect(() => {
    feedbackModalOpenRef.current = showFeedbackModal;
  }, [showFeedbackModal]);
  
  // Instructor state
  const [instructor, setInstructor] = useState<any>(null);
  const [instructorLoading, setInstructorLoading] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>('');
  const [filteredInstructors, setFilteredInstructors] = useState<any[]>([]);
  const [showManualOption, setShowManualOption] = useState<boolean>(false);
  
  // Other state
  const [isUpdatingStatus, setIsUpdatingStatus] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  
  // Get training notes data from props
  const trainingNotesData = propNotesData?.[training?.id || ''];
  const data = trainingNotesData;
  
  // Extract partners from notesData if available
  const partnersFromData = data?.partners || [];
  
  // Use existing hooks
  const { partners, loading: partnersLoading, saving: partnersSaving, addPartner, removePartner, savePartners } = useTrainingPartnersFromData(
    training?.id || '',
    partnersFromData,
    partnersRefreshKey
  );
  
  const { getTrainingFeedbacks } = usePartnerFeedback({
    trainingId: data?.notes?.id || training?.id
  });
  
  const { data: techniqueCategories, isLoading: techniquesLoading } = useTechniques();
  
  // Sync local partners with hook partners
  useEffect(() => {
    setLocalPartners(partners);
  }, [partners]);
  
  // Load training notes data when modal opens (BEFORE reset)
  // This ensures saved techniques are loaded before any reset happens
  useEffect(() => {
    const trainingId = training?.id;
    
    if (isOpen && trainingId && data?.notes) {
      // Only load from server data if:
      // 1. This is the first time loading for this training, OR
      // 2. The current fullNotes is empty (user hasn't typed anything yet)
      const shouldLoadFromServer = 
        hasLoadedInitialDataRef.current !== trainingId || 
        fullNotes === '';
      
      if (shouldLoadFromServer) {
        setFullNotes(data.notes.full_notes || '');
        setShareWithCommunity(data.notes.shared_with_community || false);
        setPrivacyLevel(data.notes.privacy_level || 'public');
        setOverallRating(data.notes.overall_rating || null);
        hasLoadedInitialDataRef.current = trainingId;
      }
      
      // CRITICAL: Load techniques from data.techniques
      // Only load if techniqueCategories are available (they're needed to convert names to IDs)
      if (data.techniques && Array.isArray(data.techniques) && techniqueCategories && techniqueCategories.length > 0) {
        const techniqueIds = data.techniques.map((tech: any) => {
          const categoryName = tech.category || '';
          const category = techniqueCategories.find((cat: any) => cat.name === categoryName);
          if (category && category.techniques) {
            const techIndex = category.techniques.findIndex((t: any) => t.name === tech.name);
            if (techIndex !== undefined && techIndex >= 0) {
              return `${categoryName}_${techIndex + 1}`;
            }
          }
          return '';
        }).filter(Boolean);
        setSelectedTechniques(techniqueIds);
      }
    } else if (isOpen && trainingId && !data?.notes) {
      // Reset form fields only if no saved notes exist
      if (hasLoadedInitialDataRef.current !== trainingId) {
        setFullNotes('');
        setShareWithCommunity(false);
        setPrivacyLevel('public');
        setOverallRating(null);
        setSelectedTechniques([]);
        hasLoadedInitialDataRef.current = trainingId;
      }
    } else if (!isOpen) {
      // Reset ref when modal closes
      hasLoadedInitialDataRef.current = null;
    }
  }, [isOpen, training?.id, data, techniqueCategories, fullNotes]);

  // Use ref to track previous training ID to detect actual changes
  const previousTrainingIdRef = useRef<string | null>(null);
  const previousIsOpenRef = useRef<boolean>(false);

  // Reset UI state when modal opens (but not when feedback modal is open)
  useEffect(() => {
    // Only reset if this is a new modal open (isOpen changed from false to true)
    // OR if training ID actually changed (not just a re-render due to query invalidation)
    const isNewOpen = isOpen && !previousIsOpenRef.current;
    const isTrainingChanged = training?.id && training.id !== previousTrainingIdRef.current;
    
    if (isOpen && training?.id && (isNewOpen || isTrainingChanged)) {
      // Don't reset if feedback modal is open - this prevents resetting state
      // when queries are invalidated during feedback modal interaction
      if (showFeedbackModal) {
        // Update refs but don't reset state
        previousIsOpenRef.current = isOpen;
        previousTrainingIdRef.current = training.id;
        return;
      }
      
      // Always reset UI state (not form data - that's handled above)
      // Reset rollCategories when training changes to prevent data from different trainings mixing
      if (isTrainingChanged) {
        setRollCategories(new Map());
      }
      setShowTechniques(false);
      setOpenCategories([]);
      setPartnerSearchValue(null);
      setInstructor(null);
      setPartnerNotes(new Map());
      setRollCategoriesOpenCategories(new Map());
      setShowRollCategories(new Map());
      setCurrentFeedbackPartner(null);
      setExistingFeedback(null);
      setShowFeedbackModal(false);
      
      setPartnersRefreshKey(prev => prev + 1);
      
      if (training?.id) {
        getTrainingFeedbacks(training.id).catch(error => {
          console.error('Error loading feedbacks:', error);
        });
      }
      
      // Update refs after reset
      previousIsOpenRef.current = isOpen;
      previousTrainingIdRef.current = training.id;
    } else if (isOpen && training?.id) {
      // Just update refs if modal is already open and training hasn't changed
      previousIsOpenRef.current = isOpen;
      previousTrainingIdRef.current = training.id;
    } else if (!isOpen) {
      // Reset refs when modal closes
      previousIsOpenRef.current = false;
      previousTrainingIdRef.current = null;
    }
  }, [isOpen, training?.id, getTrainingFeedbacks, showFeedbackModal]);
  
  // Load partner notes when partners are loaded
  useEffect(() => {
    if (isOpen && training?.id && localPartners.length > 0) {
      const loadAllPartnerNotes = async (): Promise<void> => {
        setReloadingPartnerNotes(true);
        try {
          const notesMap = new Map<string, any>();
          
          const rollCategoriesMap = new Map<string, import('@/features/calendar/components/RollCategoriesSection').IRollCategories>();
          
          for (const partner of localPartners) {
            const partnerKey = partner.partner_user_id || partner.id;
            // Use composite key: training_id + partner_key to isolate data per training
            const compositeKey = `${training.id}_${partnerKey}`;
            // Use usePartnerNotes hook instead of direct fetch
            // For now, we'll create individual hook instances per partner
            // In a future refactor, we could batch this
            const notes = await fetchPartnerNotesForPartner(partner);
            notesMap.set(partnerKey, notes);
            
            // Load roll categories from notes if they exist
            // Use composite key to ensure data is isolated per training
            // Check current state in the hook's rollCategories Map using composite key
            const existingInHook = rollCategories.get(compositeKey);
            const hasExistingData = existingInHook && Object.keys(existingInHook).length > 0 && 
              Object.values(existingInHook).some(v => v && typeof v === 'string' && v.trim() !== '');
            
            // Always check for roll_categories in notes, even if it's an empty object
            if (notes && notes.roll_categories !== undefined && notes.roll_categories !== null) {
              // Ensure it's an object, not a string (in case it's stored as JSON string)
              const parsedRollCategories = typeof notes.roll_categories === 'string' 
                ? JSON.parse(notes.roll_categories) 
                : notes.roll_categories;
              const hasDbData = parsedRollCategories && typeof parsedRollCategories === 'object' && 
                Object.keys(parsedRollCategories).length > 0 &&
                Object.values(parsedRollCategories).some(v => v && typeof v === 'string' && v.trim() !== '');
              
              // Priority: existing hook data > DB data > empty
              // Use composite key to ensure data is isolated per training
              if (hasExistingData) {
                rollCategoriesMap.set(compositeKey, existingInHook);
              } else if (hasDbData) {
                rollCategoriesMap.set(compositeKey, parsedRollCategories);
              } else {
                rollCategoriesMap.set(compositeKey, {});
              }
            } else {
              // Don't set empty object if we already have data in the hook
              if (hasExistingData) {
                rollCategoriesMap.set(compositeKey, existingInHook);
              } else {
                // Only set empty if hook doesn't have this key yet
                rollCategoriesMap.set(compositeKey, {});
              }
            }
          }
          setPartnerNotes(notesMap);
          setRollCategories(rollCategoriesMap);
        } catch (error) {
          console.error('❌ Error loading partner notes:', error);
        } finally {
          setReloadingPartnerNotes(false);
        }
      };
      loadAllPartnerNotes();
    }
  }, [isOpen, training?.id, localPartners]);
  
  // Helper function to fetch partner notes using the hook pattern
  const fetchPartnerNotesForPartner = async (partner: TrainingPartner): Promise<any> => {
    if (!training?.id || (!partner.partner_user_id && !partner.id)) {
      return null;
    }
    
    try {
      // Use dynamic import to avoid circular dependencies
      const { supabase } = await import('@/integrations/supabase/client');
      const baseUrl = import.meta.env.DEV
        ? 'http://localhost:54321'
        : import.meta.env.VITE_SUPABASE_URL;
        
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const response = await fetch(`${baseUrl}/functions/v1/get_partner_notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          training_id: training.id,
          partner_user_id: partner.partner_user_id,
          partner_id: partner.id,
        }),
      });

      if (!response.ok) {
        return null;
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('❌ Error fetching partner notes:', error);
      return null;
    }
  };
  
  // Function to check if partner has notes
  const hasPartnerNotes = useCallback((partner: TrainingPartner): boolean => {
    const key = partner.partner_user_id || partner.id;
    return partnerNotes.has(key) && partnerNotes.get(key) !== null;
  }, [partnerNotes]);
  
  // Function to get partner notes
  const getPartnerNotes = useCallback((partner: TrainingPartner): any => {
    const key = partner.partner_user_id || partner.id;
    return partnerNotes.get(key) || null;
  }, [partnerNotes]);
  
  // Function to update partner notes in the Map (for when fetching outside the hook)
  const updatePartnerNotes = useCallback((partnerKey: string, notes: any): void => {
    setPartnerNotes(prev => {
      const newMap = new Map(prev);
      newMap.set(partnerKey, notes);
      return newMap;
    });
    
    // Also update roll categories from notes if they exist
    // Use composite key: training_id + partner_key to isolate data per training
    if (notes && notes.roll_categories && training?.id) {
      // Ensure it's an object, not a string (in case it's stored as JSON string)
      const parsedRollCategories = typeof notes.roll_categories === 'string' 
        ? JSON.parse(notes.roll_categories) 
        : notes.roll_categories;
      const compositeKey = `${training.id}_${partnerKey}`;
      setRollCategories(prev => {
        const newMap = new Map(prev);
        newMap.set(compositeKey, parsedRollCategories || {});
        return newMap;
      });
    }
  }, [training?.id]);
  
  // Function to get roll categories for a partner
  const getRollCategories = useCallback((partner: TrainingPartner): import('@/features/calendar/components/RollCategoriesSection').IRollCategories => {
    if (!training?.id) {
      return {};
    }
    const partnerKey = partner.partner_user_id || partner.id;
    // Use composite key: training_id + partner_key to isolate data per training
    const compositeKey = `${training.id}_${partnerKey}`;
    const rawValue = rollCategories.get(compositeKey);
    const categories = rawValue || {};
    return categories;
  }, [rollCategories, training?.id]);
  
  // Function to save roll categories to database
  const saveRollCategoriesToDatabase = useCallback(async (partnerKey: string, categories: import('@/features/calendar/components/RollCategoriesSection').IRollCategories): Promise<void> => {
    if (!training?.id) {
      return;
    }

    // Find the partner by key
    const partner = localPartners.find(p => (p.partner_user_id || p.id) === partnerKey);
    if (!partner) {
      return;
    }

    try {
      const { supabase } = await import('@/integrations/supabase/client');
      const baseUrl = import.meta.env.DEV
        ? 'http://localhost:54321'
        : import.meta.env.VITE_SUPABASE_URL;
        
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      // Filter out empty values
      const cleanedRollCategories = Object.fromEntries(
        Object.entries(categories).filter(([_, value]) => value && typeof value === 'string' && value.trim() !== '')
      );

      const requestData = {
        training_id: training.id,
        partner_user_id: partner.partner_user_id,
        partner_id: partner.partner_user_id ? undefined : partner.id,
        personal_techniques_applied: [],
        personal_notes: '',
        partner_techniques_applied: [],
        feedback_notes: '',
        roll_categories: cleanedRollCategories,
      };

      const response = await fetch(`${baseUrl}/functions/v1/save_partner_notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save roll categories: ${response.status} ${errorText}`);
      }

      const result = await response.json();
      if (result.error) {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error saving roll categories:', error);
    }
  }, [training?.id, localPartners]);

  // Function to handle roll category change
  const handleRollCategoryChange = useCallback((partnerKey: string, category: string, value: string): void => {
    if (!training?.id) {
      return;
    }
    // Use composite key: training_id + partner_key to isolate data per training
    const compositeKey = `${training.id}_${partnerKey}`;
    setRollCategories(prev => {
      const newMap = new Map(prev);
      const current = newMap.get(compositeKey) || {};
      const updated = {
        ...current,
        [category]: value,
      };
      newMap.set(compositeKey, updated);
      
      // Save to database automatically
      saveRollCategoriesToDatabase(partnerKey, updated);
      
      return newMap;
    });
  }, [saveRollCategoriesToDatabase, training?.id]);
  
  // Function to set open categories for roll categories
  const setRollCategoriesOpenCategoriesForPartner = useCallback((partnerKey: string, categories: string[] | ((prev: string[]) => string[])): void => {
    setRollCategoriesOpenCategories(prev => {
      const newMap = new Map(prev);
      const current = newMap.get(partnerKey) || [];
      const newCategories = typeof categories === 'function' ? categories(current) : categories;
      newMap.set(partnerKey, newCategories);
      return newMap;
    });
  }, []);
  
  // Function to get show/hide state for roll categories
  const getShowRollCategories = useCallback((partner: TrainingPartner): boolean => {
    const key = partner.partner_user_id || partner.id;
    return showRollCategories.get(key) || false;
  }, [showRollCategories]);
  
  // Function to set show/hide state for roll categories
  const setShowRollCategoriesForPartner = useCallback((partnerKey: string, show: boolean | ((prev: boolean) => boolean)): void => {
    setShowRollCategories(prev => {
      const newMap = new Map(prev);
      const current = newMap.get(partnerKey) || false;
      const newShow = typeof show === 'function' ? show(current) : show;
      newMap.set(partnerKey, newShow);
      return newMap;
    });
  }, []);

  // Function to reload partner notes after save
  const reloadPartnerNotes = useCallback(async (partnerKey: string): Promise<void> => {
    setReloadingPartnerNotes(true);
    try {
      const partner = localPartners.find(p => 
        (p.partner_user_id || p.id) === partnerKey
      );
      
      if (!partner) return;
      
      const isManualPartner = partner.is_manual || !partner.partner_user_id;
      const partnerUserId = isManualPartner ? null : partnerKey;
      const partnerId = isManualPartner ? partnerKey : undefined;
      
      const notes = await fetchPartnerNotesForPartner(partner);
      
      setPartnerNotes(prev => {
        const newMap = new Map(prev);
        newMap.set(partnerKey, notes);
        return newMap;
      });
    } catch (error) {
      console.error('Error reloading partner notes:', error);
    } finally {
      setReloadingPartnerNotes(false);
    }
  }, [localPartners]);
  
  // Handle partner select
  const handlePartnerSelect = useCallback((partner: TrainingPartner | string | null): void => {
    if (!partner) {
      setPartnerSearchValue(null);
      return;
    }
    
    // Handle manual partner (string)
    if (typeof partner === 'string') {
      const manualPartner: TrainingPartner = {
        id: `manual-${Date.now()}`,
        manual_partner_name: partner,
        partner_name: partner,
        is_manual: true,
      };
      
      const isDuplicate = localPartners.some(p => 
        p.manual_partner_name === manualPartner.manual_partner_name
      );
      
      if (!isDuplicate) {
        addPartner(manualPartner);
      }
      setPartnerSearchValue(null);
      return;
    }
    
    // Handle registered user partner
    // PartnerAutocomplete returns Partner with: id, full_name, username, profile_picture_url, belt_rank
    // TrainingPartner expects: partner_user_id, partner_name, partner_username, partner_avatar_url, partner_belt_rank
    // Check if partner already has TrainingPartner format (from API) or Partner format (from autocomplete)
    const isTrainingPartnerFormat = 'partner_name' in partner || 'partner_user_id' in partner;
    
    const convertedPartner: TrainingPartner = isTrainingPartnerFormat
      ? partner as TrainingPartner
      : {
          id: (partner as any).id || `partner-${Date.now()}`,
          partner_user_id: (partner as any).partner_user_id || (partner as any).id,
          partner_name: (partner as any).partner_name || (partner as any).full_name || 'Unknown Partner',
          partner_username: (partner as any).partner_username || (partner as any).username,
          partner_avatar_url: (partner as any).partner_avatar_url || (partner as any).profile_picture_url,
          partner_belt_rank: (partner as any).partner_belt_rank || (partner as any).belt_rank,
          is_manual: false,
        };
    
    const isDuplicate = localPartners.some(p => 
      (p.partner_user_id && p.partner_user_id === convertedPartner.partner_user_id) ||
      (p.id && p.id === convertedPartner.id) ||
      (p.manual_partner_name && p.manual_partner_name === convertedPartner.manual_partner_name)
    );
    
    if (!isDuplicate) {
      addPartner(convertedPartner);
    }
    
    setPartnerSearchValue(null);
  }, [localPartners, addPartner]);
  
  // Handle save
  const handleSave = useCallback(async (): Promise<void> => {
    if (!training) return;
    
    // Prevent multiple simultaneous saves (race condition protection)
    if (saving) {
      console.warn('Save already in progress, ignoring duplicate call');
      return;
    }

    setSaving(true);
    try {
      // Capture current values at the start of save to avoid race conditions
      const currentFullNotes = fullNotes;
      const currentShareWithCommunity = shareWithCommunity;
      const currentPrivacyLevel = privacyLevel;
      const currentOverallRating = overallRating;
      const currentSelectedTechniques = selectedTechniques;
      
      // Convert technique IDs to technique names
      const techniqueNames = currentSelectedTechniques.map(techId => {
        const lastUnderscoreIndex = techId.lastIndexOf('_');
        const categoryName = techId.substring(0, lastUnderscoreIndex);
        const indexStr = techId.substring(lastUnderscoreIndex + 1);
        const index = parseInt(indexStr) - 1;
        
        const category = (techniqueCategories as any[])?.find(cat => cat.name === categoryName);
        if (category && category.techniques && category.techniques[index]) {
          return category.techniques[index].name;
        }
        return techId;
      });

      // Save notes using Edge Function
      const { supabase } = await import('@/integrations/supabase/client');
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) throw new Error('No access token');

      const baseUrl = import.meta.env.DEV
        ? 'http://127.0.0.1:54321'
        : import.meta.env.VITE_SUPABASE_URL;

      // Save training notes
      const response = await fetch(`${baseUrl}/functions/v1/save_training_notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          training_id: training.id,
          general_notes: '',
          full_notes: currentFullNotes, // Use captured value
          shared_with_community: currentShareWithCommunity, // Use captured value
          privacy_level: currentPrivacyLevel, // Use captured value
          technique_ids: techniqueNames,
          overall_rating: currentOverallRating // Use captured value
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save notes');
      }

      const responseData = await response.json();
      
      // CRITICAL: Invalidate posts queries if a post was created
      if (responseData.post) {
        queryClient.invalidateQueries({ queryKey: ['posts'] });
        queryClient.invalidateQueries({ queryKey: ['top-posts'] });
      }

      // CRITICAL: Save partners after saving notes
      // Partners are added locally via addPartner, but need to be persisted to database
      if (localPartners.length > 0) {
        try {
          await savePartners(localPartners);
        } catch (partnerError) {
          console.error('Error saving partners:', partnerError);
          // Don't throw - notes were saved successfully, partners can be retried
          toast({
            title: "Notes saved, but partners failed",
            description: "Please try adding partners again.",
            variant: "destructive",
          });
        }
      }

      // After successful save, reset the ref so that saved data can be loaded next time
      // This ensures that if the modal is reopened, it will load the latest saved data
      if (training?.id) {
        hasLoadedInitialDataRef.current = null;
      }

      if (onDataSaved) {
        onDataSaved();
      }
    } catch (error) {
      console.error('Error saving notes:', error);
      toast({
        title: "Error saving notes",
        description: "Please try again.",
        variant: "destructive",
      });
      throw error;
    } finally {
      setSaving(false);
    }
  }, [training, selectedTechniques, techniqueCategories, fullNotes, shareWithCommunity, privacyLevel, overallRating, localPartners, savePartners, onDataSaved, toast, saving]);
  
  // Handle feedback modal close
  const handleFeedbackModalClose = useCallback(async (wasSaved = false): Promise<void> => {
    setShowFeedbackModal(false);
    
    if (!training) return;
    
    try {
      const notesId = data?.notes?.id || training.id;
      await getTrainingFeedbacks(notesId);
      
      if (data?.notes?.id) {
        queryClient.invalidateQueries({ queryKey: ['training-notes', training.id] });
      }
      
      if (wasSaved && currentFeedbackPartner) {
        setPartnersRefreshKey(prev => prev + 1);
        
        const partnerKey = currentFeedbackPartner.partner_user_id || currentFeedbackPartner.id;
        if (partnerKey) {
          await reloadPartnerNotes(partnerKey);
        }
        
        if (localPartners.length > 0) {
          for (const partner of localPartners) {
            const key = partner.partner_user_id || partner.id;
            if (key && key !== partnerKey) {
              try {
                await reloadPartnerNotes(key);
              } catch (error) {
                console.error('Error reloading partner notes:', error);
              }
            }
          }
        }
      }
      
      setTimeout(() => {
        setFeedbackReloadKey(prev => prev + 1);
      }, 50);
      
      setTimeout(() => {
        setFeedbackReloadKey(prev => prev + 1);
      }, 200);
      
      if (wasSaved) {
        toast({
          title: "Feedback saved!",
          description: "The training notes have been updated.",
        });
        
        if (onDataSaved) {
          onDataSaved();
        }
      }
    } catch (error) {
      console.error('Error updating feedbacks:', error);
      toast({
        title: "Error updating feedbacks",
        description: "Please refresh the page to see the latest changes.",
        variant: "destructive",
      });
    }
  }, [training, data, currentFeedbackPartner, localPartners, getTrainingFeedbacks, reloadPartnerNotes, queryClient, onDataSaved, toast]);
  
  return {
    // Form state
    fullNotes,
    setFullNotes,
    shareWithCommunity,
    setShareWithCommunity,
    privacyLevel,
    setPrivacyLevel,
    overallRating,
    setOverallRating,
    selectedTechniques,
    setSelectedTechniques,
    showTechniques,
    setShowTechniques,
    openCategories,
    setOpenCategories,
    
    // Partners state
    localPartners,
    partnersLoading,
    partnersSaving,
    partnerSearchValue,
    setPartnerSearchValue,
    showAllPartners,
    setShowAllPartners,
    partnerNotes,
    reloadingPartnerNotes,
    addPartner,
    removePartner,
    savePartners,
    reloadPartnerNotes,
    updatePartnerNotes,
    hasPartnerNotes,
    getPartnerNotes,
    
    // Roll categories state
    rollCategories,
    rollCategoriesOpenCategories: rollCategoriesOpenCategoriesState,
    setRollCategoriesOpenCategoriesForPartner,
    handleRollCategoryChange,
    getRollCategories,
    showRollCategories,
    setShowRollCategoriesForPartner,
    getShowRollCategories,
    
    // Feedback modal state
    currentFeedbackPartner,
    setCurrentFeedbackPartner,
    existingFeedback,
    setExistingFeedback,
    showFeedbackModal,
    setShowFeedbackModal,
    handleFeedbackModalClose,
    
    // Instructor state
    instructor,
    setInstructor,
    instructorLoading,
    setInstructorLoading,
    inputValue,
    setInputValue,
    filteredInstructors,
    setFilteredInstructors,
    showManualOption,
    setShowManualOption,
    
    // Other state
    isUpdatingStatus,
    setIsUpdatingStatus,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    saving,
    setSaving,
    
    // Data
    data,
    techniqueCategories,
    techniquesLoading,
    
    // Actions
    handleSave,
    handlePartnerSelect,
  };
};

